import { yupResolver } from "@hookform/resolvers/yup";
import {
    SearchInput,
    Popover,
    PopoverTrigger,
    PopoverContent,
    FormButton,
    Button,
    DatePicker,
    Select,
    Input,
} from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { useDebounce } from "../../../../hooks/helpers";
import {
    displayNameInRightFormat,
    handleFormatDatePickerValue,
    convertDateFilterStringToDate,
    removeEnumUnderscore,
    makeStringFirstLetterCapital,
} from "../../../../utils/helpers";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { IClient } from "../../../../utils/types/client";
import Loader from "../../../../shared-ui/Loader/Loader";
import ListState from "../../../../components/ListState/ListState";
import useToast from "../../../../hooks/useToast";
import { useCreateClientForm } from "../../hooks/mutations/client-forms.mutations";
import {
    FormStatus,
    FormType,
    IAddClientFormPayload,
} from "../../types/client-forms.types";

const FORM_TYPE_OPTIONS = Object.values(FormType).map((value) => ({
    label: makeStringFirstLetterCapital(removeEnumUnderscore(value)),
    value,
}));

const FORM_STATUS_OPTIONS = Object.values(FormStatus).map((value) => ({
    label: makeStringFirstLetterCapital(removeEnumUnderscore(value)),
    value,
}));

interface CreateClientFormFormProps {
    onFormSubmit: () => void;
}

const schema = yup.object({
    client_id: yup.string().required("Client is required"),
    form_type: yup.string().required("Form type is required"),
    status: yup.string().oneOf(Object.values(FormStatus)).optional(),
    start_date: yup.string().required("Start date is required"),
    end_date: yup.string().required("End date is required"),
    signed_at: yup.string().optional(),
    authorized_recipients: yup.string().optional(),
});

export default function CreateClientFormForm({
    onFormSubmit,
}: CreateClientFormFormProps) {
    const [clientSearchString, setClientSearchString] = React.useState("");
    const [selectedClient, setSelectedClient] = React.useState<IClient | null>(
        null
    );
    const [showClientsListPopover, setShowClientsListPopover] =
        React.useState(false);

    const clientsListContainerRef = React.useRef<HTMLDivElement>(null);
    const debouncedClientSearch = useDebounce(clientSearchString, 300);
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const createFormMutation = useCreateClientForm();

    const {
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm<IAddClientFormPayload>({
        resolver: yupResolver(schema),
        defaultValues: {
            status: FormStatus.NOT_SENT,
        },
    });

    const watchedFormType = watch("form_type");
    const watchedStatus = watch("status");

    const {
        data: clients,
        isLoading: clientsLoading,
        error: clientsError,
        isSuccess: clientsSuccess,
    } = useFetchClientList({
        page: 1,
        search_string: debouncedClientSearch,
        limit: 20,
    });

    const handleClientSelect = (client: IClient) => {
        setSelectedClient(client);
        setValue("client_id", client.client_id, { shouldValidate: true });
        setClientSearchString("");
        setShowClientsListPopover(false);
    };

    const onSubmit = (data: IAddClientFormPayload) => {
        createFormMutation.mutate(
            {
                clientId: data.client_id,
                data: {
                    form_type: data.form_type,
                    client_id: data.client_id,
                    start_date: data.start_date,
                    end_date: data.end_date,
                    status: data.status,
                    signed_at:
                        data.status === FormStatus.COMPLETED && data.signed_at
                            ? data.signed_at
                            : undefined,
                    form_content:
                        data.form_type === "release_of_information" &&
                        data.authorized_recipients?.trim()
                            ? {
                                  authorized_users: [
                                      data.authorized_recipients.trim(),
                                  ],
                              }
                            : undefined,
                },
            },
            {
                onSuccess: () => {
                    toast({
                        mode: "success",
                        message: "Form created successfully",
                    });
                    queryClient.invalidateQueries(["client-forms"]);
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err?.response?.data?.message ||
                            "Could not create form at this time",
                    });
                },
            }
        );
    };

    return (
        <form id="create-client-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
                <div ref={clientsListContainerRef}>
                    <Popover
                        open={showClientsListPopover}
                        onOpenChange={(state) =>
                            setShowClientsListPopover(state)
                        }
                    >
                        <PopoverTrigger
                            asChild
                            onClick={() => setShowClientsListPopover(true)}
                        >
                            <FormButton
                                className={
                                    selectedClient
                                        ? "capitalize"
                                        : "normal-case"
                                }
                                value={
                                    selectedClient
                                        ? displayNameInRightFormat({
                                              firstName:
                                                  selectedClient.first_name,
                                              lastName:
                                                  selectedClient.last_name,
                                          })
                                        : ""
                                }
                                placeholder="Select client..."
                                label="Client"
                                isRequired
                            />
                        </PopoverTrigger>
                        <PopoverContent
                            className="popover-full"
                            align="start"
                            container={clientsListContainerRef.current}
                        >
                            <SearchInput
                                placeholder="Search for client..."
                                onChange={(e) =>
                                    setClientSearchString(e.target.value)
                                }
                                value={clientSearchString}
                            />
                            <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                {clientsLoading && (
                                    <Loader wrapperClassName="h-[150px]" />
                                )}
                                {clientsError && (
                                    <ListState
                                        isError
                                        context="general"
                                        stateHelperText="Please try again later"
                                        errorMsg={
                                            clientsError?.response?.data
                                                ?.message ||
                                            "Error loading clients"
                                        }
                                        cardClass="h-[150px]"
                                    />
                                )}
                                {clientsSuccess &&
                                    clients?.data &&
                                    clients.data.length > 0 && (
                                        <>
                                            {clients.data.map((client) => (
                                                <Button
                                                    key={client.client_id}
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                    onClick={() =>
                                                        handleClientSelect(
                                                            client
                                                        )
                                                    }
                                                >
                                                    {displayNameInRightFormat({
                                                        firstName:
                                                            client.first_name,
                                                        lastName:
                                                            client.last_name,
                                                    })}
                                                </Button>
                                            ))}
                                            {selectedClient && (
                                                <Button
                                                    size="auto"
                                                    variant="can-hover"
                                                    className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                    onClick={() => {
                                                        setSelectedClient(null);
                                                        setValue(
                                                            "client_id",
                                                            ""
                                                        );
                                                        setClientSearchString(
                                                            ""
                                                        );
                                                        setShowClientsListPopover(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </>
                                    )}
                            </div>
                        </PopoverContent>
                    </Popover>
                    {errors.client_id && (
                        <p className="text-xs text-danger mt-1">
                            {errors.client_id.message}
                        </p>
                    )}
                </div>

                <Controller
                    name="form_type"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Form Type"
                            placeholder="Select form type..."
                            options={FORM_TYPE_OPTIONS}
                            value={
                                FORM_TYPE_OPTIONS.find(
                                    (o) => o.value === field.value
                                ) ?? null
                            }
                            onChange={(val) => {
                                const option =
                                    val as typeof FORM_TYPE_OPTIONS[0];
                                field.onChange(option?.value ?? "");
                            }}
                            hasError={!!errors.form_type}
                            errorText={errors.form_type?.message}
                            isLongListInDialog
                        />
                    )}
                />
                {watchedFormType === FormType.RELEASE_OF_INFORMATION && (
                    <Controller
                        name="authorized_recipients"
                        control={control}
                        render={({ field }) => (
                            <Input
                                {...field}
                                label="Authorized Recipient"
                                placeholder="Name of person/entity receiving info"
                            />
                        )}
                    />
                )}

                <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Initial Status"
                            placeholder="Select status..."
                            options={FORM_STATUS_OPTIONS}
                            value={
                                FORM_STATUS_OPTIONS.find(
                                    (o) => o.value === field.value
                                ) ?? FORM_STATUS_OPTIONS[0]
                            }
                            onChange={(val) => {
                                const option =
                                    val as typeof FORM_STATUS_OPTIONS[0];
                                field.onChange(
                                    option?.value ?? FormStatus.NOT_SENT
                                );
                            }}
                            isLongListInDialog
                        />
                    )}
                />

                <div>
                    <Controller
                        name="start_date"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="Start Date"
                                selected={
                                    field.value
                                        ? convertDateFilterStringToDate(
                                              field.value
                                          )
                                        : null
                                }
                                onChange={(date) =>
                                    setValue(
                                        "start_date",
                                        date
                                            ? handleFormatDatePickerValue(date)
                                            : "",
                                        { shouldValidate: true }
                                    )
                                }
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        )}
                    />
                    {errors.start_date && (
                        <p className="text-xs text-danger mt-1">
                            {errors.start_date.message}
                        </p>
                    )}
                </div>

                <div>
                    <Controller
                        name="end_date"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="End Date"
                                selected={
                                    field.value
                                        ? convertDateFilterStringToDate(
                                              field.value
                                          )
                                        : null
                                }
                                onChange={(date) =>
                                    setValue(
                                        "end_date",
                                        date
                                            ? handleFormatDatePickerValue(date)
                                            : "",
                                        { shouldValidate: true }
                                    )
                                }
                                placeholderText="MM/DD/YYYY"
                                minDate={
                                    watch("start_date")
                                        ? convertDateFilterStringToDate(
                                              watch("start_date")
                                          )
                                        : undefined
                                }
                                isClearable
                                isInDialog
                            />
                        )}
                    />
                    {errors.end_date && (
                        <p className="text-xs text-danger mt-1">
                            {errors.end_date.message}
                        </p>
                    )}
                </div>
                {watchedStatus === FormStatus.COMPLETED && (
                    <Controller
                        name="signed_at"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label="Signed At"
                                selected={
                                    field.value
                                        ? convertDateFilterStringToDate(
                                              field.value
                                          )
                                        : null
                                }
                                onChange={(date) =>
                                    setValue(
                                        "signed_at",
                                        date
                                            ? handleFormatDatePickerValue(date)
                                            : ""
                                    )
                                }
                                placeholderText="MM/DD/YYYY"
                                isClearable
                                isInDialog
                            />
                        )}
                    />
                )}
            </div>
        </form>
    );
}
